import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compact, get, find, filter, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import cx from 'classnames';
import { hasPaysClaimsCodes } from 'src/common/utils/FeatureFlags/flags';
import { fetchFileUploads as fetchingFileUploads } from 'src/actions/FileUploads';
import CardDataItem from './CardDataItem';
import PaymentDetails from './PaymentDetails';
import ClaimsCodes from './ClaimsCodes';
import ServiceDates from './ServiceDates';
import MetafieldDetails from './MetafieldDetails';
import FileMetadata from './FileMetadata';
import './CardData.scss';

const CardData = ({
  capInformation,
  className,
  metafields,
  providedService,
  paymentType,
  unit,
  fetchFileUploads,
  iconStyle,
  fileStatus,
  fileUploads,
  showClaimsCodes,
}) => {
  const {
    starts_at,
    ends_at,
    notes,
    service_duration_start_time,
    service_duration,
  } = providedService;

  const metaFileFields = filter(metafields, ['type', 'file']);
  const metaFileData = compact(metaFileFields.map((field) => find(providedService.metadata, ['field', field.field])));
  const metaFileObj = !isEmpty(metaFileData) ? compact(metaFileFields.map((field) => {
    const value = get(find(providedService.metadata, ['field', field.field]), 'value', []);
    // if metadata 'field' does not exist or metadata 'value' does not exist or is empty
    if (isEmpty(value)) {
      return null;
    }
    const files = compact(value.map((uuid) => find(fileUploads, ['id', uuid])));

    return {
      value: files,
      label: field.label,
    };
  })) : [];
  const metaFileMapping = {};
  metaFileObj.forEach((item) => {
    metaFileMapping[item.label] = true;
  });

  useEffect(() => {
    if (!isEmpty(metaFileObj)) {
      fetchFileUploads({ recordId: providedService.id, recordType: 'provided_service' });
    }
  }, [fetchFileUploads, providedService.id]);

  return (
    <div className={cx(className, 'card-data')}>
      <div className="antialiased card-data__container space-y-2">
        <PaymentDetails
          capInformation={capInformation}
          paymentType={paymentType}
          providedService={providedService}
          unit={unit}
        />
        <CardDataItem
          dataLabel="service-period-select-value"
          label="Period of Service"
          value={!providedService.ends_at ? 'Single Date' : 'Date Range'}
        />
        {showClaimsCodes && (
          <ClaimsCodes providedService={providedService} />
        )}
        <ServiceDates
          startsAt={starts_at}
          endsAt={ends_at}
          serviceDurationStartTime={service_duration_start_time}
          serviceDuration={service_duration}
          paymentType={paymentType}
        />
        <MetafieldDetails metafields={metafields} metadata={providedService.metadata} />
        {notes && (
          <>
            <hr className="border-dark-fill-blue" />
            <CardDataItem
              dataLabel="service-note-value"
              label="Note"
              value={providedService.notes}
            />
          </>
        )}
        <FileMetadata
          fileIconStyle={iconStyle}
          fileStatus={fileStatus}
          metaFileObj={metaFileObj}
          metaFileFields={metaFileFields}
          metaFileMapping={metaFileMapping}
        />
      </div>
    </div>
  );
};

CardData.propTypes = {
  capInformation: PropTypes.string,
  className: PropTypes.string,
  fetchFileUploads: PropTypes.func.isRequired,
  fileStatus: PropTypes.string.isRequired,
  fileUploads: PropTypes.array,
  iconStyle: PropTypes.object,
  metafields: PropTypes.array,
  paymentType: PropTypes.string,
  providedService: PropTypes.object.isRequired,
  showClaimsCodes: PropTypes.bool,
  unit: PropTypes.string,
};

CardData.defaultProps = {
  capInformation: '',
  className: '',
  fileUploads: [],
  iconStyle: {
    svg: {
      height: '18px',
      width: '14px',
      fill: '#4467AB',
    },
  },
  metafields: [],
  paymentType: '',
  showClaimsCodes: false,
  unit: '',
};

const mapStateToProps = (state) => {
  const fileUploads = get(state, 'fileUploads.fileUploads');
  const fileStatus = get(state, 'fileUploads.status');

  return {
    fileUploads,
    fileStatus,
    showClaimsCodes: hasPaysClaimsCodes(state),
  };
};

export default connect(mapStateToProps, {
  fetchFileUploads: fetchingFileUploads,
})(CardData);
